import React from 'react';
import { View, ScrollView, SafeAreaView, ActivityIndicator, StyleSheet } from 'react-native';
import { heightPercentageToDP as hp, widthPercentageToDP as wp } from 'react-native-responsive-screen';

import CustomText from '../CustomText';
import Util from '../../util/util';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';
import { ANONYMOUS_REPORTER } from '../../util/reports';

/**
 * ReportContent - Displays the main report content (description, timestamp, reporter)
 *
 * Shared between ReportDetails.js and AdminReportDetails.js
 *
 * @param {Object} props
 * @param {string} props.content - The report description/content
 * @param {number} props.timestamp - The report timestamp
 * @param {string} props.reporter - The reporter name (or empty for anonymous)
 * @param {boolean} props.loading - Whether the report is still loading
 */
const ReportContent = ({ content, timestamp, reporter, loading = false }) => {
  const { t } = useTranslation('reports');

  if (loading || !content) {
    return (
      <SafeAreaView>
        <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView>
      {/* V3: Card-style content container */}
      <View style={styles.contentCard}>
        <ScrollView style={{}} contentContainerStyle={{}}>
          <CustomText font="regular" size={16} color={AppStyles.colorSet.textPrimary}>
            {content}
          </CustomText>
        </ScrollView>
      </View>
      {/* V3: Metadata with muted colors */}
      <View style={styles.metadataContainer}>
        <CustomText
          font="regular"
          size={14}
          color={AppStyles.colorSet.textSecondary}
        >
          {Util.timeConverter(timestamp)}
        </CustomText>
        <CustomText
          font="regular"
          size={14}
          color={AppStyles.colorSet.textSecondary}
        >
          {reporter && reporter !== ANONYMOUS_REPORTER ? reporter : t('details.anonymous')}
        </CustomText>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // V3: Clean white card with visible border - matches Submit Report card styling
  contentCard: {
    borderRadius: 16,
    backgroundColor: AppStyles.colorSet.cardBackground,
    paddingVertical: 16,
    paddingHorizontal: wp(4),
    marginTop: hp(1),
    maxHeight: hp(40),
    minHeight: hp(15),
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  metadataContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: hp(1),
    paddingHorizontal: 4,
  },
});

export default ReportContent;
